import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * Composant StructuredData pour générer le JSON-LD Schema.org
 * 
 * @param {string} type - Type de structured data ('organization', 'softwareApplication', 'faqPage', etc.)
 * @param {object} data - Données spécifiques au type
 */
const StructuredData = ({ type, data }) => {
  const generateOrganizationSchema = () => ({
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Assistance Leagora',
    url: 'https://assistance.leagora.io',
    logo: 'https://assistance.leagora.io/logo512.png',
    description: 'Solution d\'assistance vidéo à distance pour dépanner vos clients sans rien installer',
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'support@leagora.fr',
      contactType: 'Customer Service',
      availableLanguage: ['French', 'English']
    },
    sameAs: [
      'https://www.linkedin.com/company/leagora'
    ]
  });

  const generateSoftwareApplicationSchema = () => ({
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Assistance Leagora',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web, iOS, Android',
    offers: {
      '@type': 'AggregateOffer',
      priceCurrency: 'EUR',
      lowPrice: '25',
      highPrice: '60',
      offers: [
        {
          '@type': 'Offer',
          name: 'Start',
          price: '25.00',
          priceCurrency: 'EUR',
          billingIncrement: 'Monthly',
          description: '1 agent d\'assistance, 500 équipements, Qualité HD'
        },
        {
          '@type': 'Offer',
          name: 'Business',
          price: '40.00',
          priceCurrency: 'EUR',
          billingIncrement: 'Monthly',
          description: '2 agents d\'assistance, 1000 équipements, Géolocalisation GPS'
        },
        {
          '@type': 'Offer',
          name: 'Enterprise',
          price: '60.00',
          priceCurrency: 'EUR',
          billingIncrement: 'Monthly',
          description: '5 agents d\'assistance, 1500 équipements, API dédiée'
        }
      ]
    },
    featureList: [
      'Vidéo HD adaptative',
      'Génération QR codes',
      'Géolocalisation GPS',
      'File d\'attente en temps réel',
      'Aucune installation requise',
      'Support multi-agents'
    ]
  });

  const generateFAQPageSchema = (faqItems) => ({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map(item => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer
      }
    }))
  });

  const generateArticleSchema = (articleData) => ({
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: articleData.headline,
    description: articleData.description,
    author: {
      '@type': 'Organization',
      name: articleData.authorName || 'Assistance Leagora',
      url: articleData.authorUrl || 'https://leagora.io',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Assistance Leagora',
      url: 'https://assistance.leagora.io',
      logo: {
        '@type': 'ImageObject',
        url: 'https://assistance.leagora.io/logo512.png',
      },
    },
    datePublished: articleData.datePublished,
    dateModified: articleData.dateModified || articleData.datePublished,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': articleData.url,
    },
  });

  const generateHowToSchema = (howToData) => ({
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: howToData.name,
    description: howToData.description,
    step: (howToData.steps || []).map((step, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      name: step.name,
      text: step.text,
    })),
  });

  const generateWebSiteSchema = () => ({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Assistance Leagora',
    url: 'https://assistance.leagora.io',
    inLanguage: ['fr-FR', 'en-US'],
    publisher: {
      '@type': 'Organization',
      name: 'Assistance Leagora',
      url: 'https://assistance.leagora.io',
    },
  });

  const generateBreadcrumbListSchema = (breadcrumbData) => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: (breadcrumbData?.items || []).map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  });

  const getSchema = () => {
    switch (type) {
      case 'organization':
        return generateOrganizationSchema();
      case 'softwareApplication':
        return generateSoftwareApplicationSchema();
      case 'faqPage':
        return generateFAQPageSchema(data?.faqItems || []);
      case 'article':
        return generateArticleSchema(data || {});
      case 'howTo':
        return generateHowToSchema(data || {});
      case 'website':
        return generateWebSiteSchema();
      case 'breadcrumbList':
        return generateBreadcrumbListSchema(data);
      default:
        return null;
    }
  };

  const schema = getSchema();

  if (!schema) return null;

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

/**
 * Composant pour générer plusieurs schemas à la fois
 */
export const MultipleStructuredData = ({ schemas }) => {
  return (
    <>
      {schemas.map((schemaConfig, index) => (
        <StructuredData 
          key={index} 
          type={schemaConfig.type} 
          data={schemaConfig.data} 
        />
      ))}
    </>
  );
};

export default StructuredData;








